import React, { FC, MutableRefObject, ReactElement, useContext, useEffect, useMemo, useState } from 'react'
import useIsMountedRef from 'use-is-mounted-ref'
import { useHotkeys } from 'react-hotkeys-hook'
import FormRow from '../../Common/Components/FormRow/FormRow'
import { ValidationErrors } from '../../Common/Components/ValidationErrors/ValidationErrors'
import {
    AI_ASSIST_ENABLED_FIELD_NAME,
    BILLABLE_FIELD_NAME,
    BILLING_ID_FIELD_NAME,
    CME_FIELD_NAME,
    DESCRIPTION_FIELD_NAME,
    ECCR_REPLACEMENT_FIELD_NAME,
    ID_FIELD_NAME,
    MFA_FIELD_NAME,
    TAGS_FIELD_NAME,
    TRIAL_FIELD_NAME,
    TYPE_FIELD_NAME
} from '../Constants/FieldNames'
import CLanguageHandler from '../../../NonReact/Languages/LanguageHandler'
import { LanguageContext, MessageContext, ReactState, UserInfoContext } from '../../Common/Contexts'
import { TelSysConfiguration } from '../../../../i18n'
import { TelSysForm } from '../Types/Types'
import { AKIXI_CME_NAME, DEFAULT_CME_ID } from '../../Common/Constants/ServerValues'
import { isStringNotEmpty, isVarNotEmpty } from '../../../NonReact/utils/GlobalFunctions'
import { SelectOptions } from '../../Common/Components/Selects/Types'
import { useFormContext, UseFormMethods } from '../../../Vendor/react-hook-form/dist/index.ie11'
import { getCMEsAsSelectOptions } from '../../Common/API/ItemsGetters'
import { handleUnknownForAvailables } from '../../Common/Functions/CommonFunction'
import { MessageContextType } from '../../Common/Contexts/MessageContext'
import { StatusOutput } from '../../Common/Components/StatusOutput/StatusOutput'
import { LicenceValue } from '../../Common/Types/Types'
import { useLicenceValue } from '../../Common/Hooks/useLicenceValue'
import { convertLicenceBooleanToBoolean } from '../../../NonReact/utils/LicenceUtils'
import { UserInfoContextType } from '../../Common/Contexts/UserInfoContext'
import { TelSysTypes } from '../../../NonReact/constants/TelephonyServerTypes'
import { useFeatureFlag } from '../../Common/Hooks/useFeatureFlag'
import Icon, { IconName } from '../../Common/Components/Icon'
import { Rules } from '../../Common/Rules/Rules'
import { AdminBackendPermissionNames } from '../../Common/Constants/Permissions'
import IconButton, { IconButtonType } from '../../Common/Components/IconButton'
import { PermissionsUtil } from '../../Common/Utils/PermissionsUtil'
import { OutlinedTextInput } from '../../../ReactRouting/Components/Atoms/Inputs/OutlinedTextInput'
import { OutlinedSelect } from '../../../ReactRouting/Components/Atoms/Inputs/OutlinedSelect'
import { RouterCheckbox } from '../../../ReactRouting/Components/Atoms/Inputs/RouterCheckbox'
import { copyToClipboard } from '../../Common/Utils/CopyToClipboard'

type Props = {
    filterCMEnvironmentID: string
    viewOnly?: boolean
    isEdit?: boolean
    commsStatus?: Nullable<string[]>
    cxiAccessCode: Nullable<string>
}

const getAvailableEntitiesDefaults = (formCMEValue?: string, filterCME?: string): SelectOptions<string> => [
    {
        label:
            isStringNotEmpty(formCMEValue) || isStringNotEmpty(filterCME)
                ? formCMEValue ?? filterCME ?? AKIXI_CME_NAME
                : AKIXI_CME_NAME,
        value: formCMEValue ?? filterCME ?? DEFAULT_CME_ID
    }
]

export const DetailsTab: FC<Props> = ({
    filterCMEnvironmentID,
    viewOnly,
    isEdit,
    commsStatus,
    cxiAccessCode
}: Props): ReactElement => {
    const language: CLanguageHandler = useContext(LanguageContext) as CLanguageHandler
    const { displayError }: MessageContextType = useContext(MessageContext)
    const userInfo: UserInfoContextType = useContext(UserInfoContext)

    const { watch }: UseFormMethods<TelSysForm> = useFormContext()

    const billingIDDisabled: Nullable<LicenceValue> = useLicenceValue('BillingTagDisableTelSrvs')

    const showTeamsTrialOption: Definable<boolean> = useFeatureFlag('showTeamsTelsysTrial')

    const cmeValue: string = watch(CME_FIELD_NAME)

    const cmeDefaults: SelectOptions<string> = useMemo(
        (): SelectOptions<string> => getAvailableEntitiesDefaults(cmeValue, filterCMEnvironmentID),
        [cmeValue, filterCMEnvironmentID]
    )

    const isBillableEnabled: boolean = useMemo(
        (): boolean => !!userInfo?.Permissions?.Admin?.includes(AdminBackendPermissionNames.CHANGE_TELSYS_BILLABLE),
        [userInfo]
    )

    const isBillingIDDisabled: boolean = useMemo(
        (): boolean => convertLicenceBooleanToBoolean(billingIDDisabled?.Value),
        [billingIDDisabled]
    )

    const [availableCMEs, setAvailableCMEs]: ReactState<SelectOptions<string>> =
        useState<SelectOptions<string>>(cmeDefaults)
    const [isLoading, setIsLoading]: ReactState<boolean> = useState<boolean>(false)
    const [isBillingIDHotkeyEnabled, setIsBillingIDHotkeyEnabled]: ReactState<boolean> = useState<boolean>(false)
    const [isECCRReplacementEnabled, setIsECCRReplacementEnabled]: ReactState<boolean> = useState<boolean>(false)

    const isMounted: MutableRefObject<boolean> = useIsMountedRef()

    const telSysTypeValue: TelSysTypes = watch(TYPE_FIELD_NAME)
    const billableValue: boolean = watch(BILLABLE_FIELD_NAME)
    const trialValue: number = watch(TRIAL_FIELD_NAME)

    const isTrialModeShown: boolean = useMemo(
        (): boolean =>
            telSysTypeValue === TelSysTypes.MS_TEAMS ||
            telSysTypeValue === TelSysTypes.BROADSOFT_BROADWORKS ||
            telSysTypeValue === TelSysTypes.WEBEX_CALLING,
        [telSysTypeValue]
    )

    const isTelSysTypeBroadworks: boolean = useMemo(
        (): boolean => telSysTypeValue === TelSysTypes.BROADSOFT_BROADWORKS,
        [telSysTypeValue]
    )

    const isUserCMEWide: boolean = useMemo(
        (): boolean => PermissionsUtil.isCMEWideUser(userInfo),
        [JSON.stringify(userInfo)]
    )

    const isAiAssistShown: boolean = useMemo(
        (): boolean =>
            telSysTypeValue === TelSysTypes.MS_TEAMS ||
            telSysTypeValue === TelSysTypes.BROADSOFT_BROADWORKS ||
            telSysTypeValue === TelSysTypes.WEBEX_CALLING ||
            telSysTypeValue === TelSysTypes.SIMULATOR,
        [telSysTypeValue]
    )

    const isRootCMEAdmin: boolean = PermissionsUtil.isRootCMEAdmin(userInfo)

    useHotkeys(
        'ctrl+alt+b',
        () => {
            setIsBillingIDHotkeyEnabled((billingHotkeyEnabled: boolean): boolean => !billingHotkeyEnabled)
            setIsECCRReplacementEnabled((value: boolean): boolean => !value)
        },
        { enableOnFormTags: true }
    )

    useEffect((): void => {
        setIsLoading(true)

        getCMEsAsSelectOptions()
            .then((cmes: SelectOptions<string>): void => {
                if (isMounted.current) {
                    handleUnknownForAvailables(cmes, setAvailableCMEs, cmeValue)
                    setIsLoading(false)
                }
            })
            .catch((e: Error): void => {
                if (isMounted.current) {
                    displayError(e)
                    setIsLoading(false)
                }
            })
    }, [])

    return (
        <div className='details-tab tab-area'>
            <FormRow>
                <ValidationErrors name={ID_FIELD_NAME}>
                    <OutlinedTextInput
                        inputName={ID_FIELD_NAME}
                        placeholder={language.get(TelSysConfiguration.TelSysConfiguration_Field_ID_Placeholder)}
                        labelLeft={language.get(TelSysConfiguration.TelSysConfiguration_Field_ID_Label)}
                        disabled={viewOnly || isEdit}
                        rules={{
                            validate: {
                                isPositive: Rules.isPositiveNumber(language),
                                isInteger: Rules.isInteger(language)
                            }
                        }}
                    />
                </ValidationErrors>
            </FormRow>
            <FormRow>
                <ValidationErrors name={DESCRIPTION_FIELD_NAME}>
                    <OutlinedTextInput
                        inputName={DESCRIPTION_FIELD_NAME}
                        labelLeft={language.get(TelSysConfiguration.TelSysConfiguration_Field_Description_Label)}
                        disabled={viewOnly}
                        rules={{
                            required: language.get(TelSysConfiguration.TelSysConfiguration_Error_Required)
                        }}
                    />
                </ValidationErrors>
            </FormRow>
            <FormRow className='central-management-environment'>
                <OutlinedSelect
                    disabled={viewOnly}
                    name={CME_FIELD_NAME}
                    label={language.get(TelSysConfiguration.TelSysConfiguration_Field_CME_Label)}
                    options={availableCMEs}
                    noChange={isLoading}
                />
            </FormRow>
            <FormRow>
                <ValidationErrors name={TAGS_FIELD_NAME}>
                    <OutlinedTextInput
                        inputName={TAGS_FIELD_NAME}
                        labelLeft={language.get(TelSysConfiguration.TelSysConfiguration_Field_Tags_Label)}
                        disabled={viewOnly || isEdit}
                    />
                </ValidationErrors>
            </FormRow>
            <FormRow>
                <ValidationErrors name={BILLING_ID_FIELD_NAME}>
                    <OutlinedTextInput
                        inputName={BILLING_ID_FIELD_NAME}
                        labelLeft={language.get(TelSysConfiguration.TelSysConfiguration_Field_BillingID_Label)}
                        disabled={viewOnly || (isBillingIDDisabled && !isBillingIDHotkeyEnabled)}
                    />
                </ValidationErrors>
            </FormRow>
            <FormRow>
                <ValidationErrors name={BILLABLE_FIELD_NAME}>
                    <RouterCheckbox
                        name={BILLABLE_FIELD_NAME}
                        labelLeft={language.get(TelSysConfiguration.TelSysConfiguration_Field_Billable_Label)}
                        disabled={viewOnly || !isBillableEnabled}
                    />
                </ValidationErrors>
            </FormRow>
            {showTeamsTrialOption ? (
                <>
                    {isTrialModeShown && !isEdit ? (
                        <FormRow>
                            <RouterCheckbox
                                name={TRIAL_FIELD_NAME}
                                labelLeft={language.get(TelSysConfiguration.TelSysConfiguration_Field_Trial_Label)}
                                disabled={viewOnly || !billableValue}
                            />
                        </FormRow>
                    ) : (
                        <></>
                    )}
                    {isTrialModeShown && isEdit && trialValue !== 0 ? (
                        <div className='trial-remaining-period'>
                            <Icon className='trial-icon' iconName={IconName.INFO} />
                            <div className='trial-text'>
                                {language.getInterpolated(
                                    TelSysConfiguration.TelSysConfiguration_Field_Trial_Text,
                                    trialValue
                                )}
                            </div>
                        </div>
                    ) : (
                        <></>
                    )}
                </>
            ) : (
                <></>
            )}
            {isTelSysTypeBroadworks ? (
                <FormRow>
                    <RouterCheckbox
                        name={ECCR_REPLACEMENT_FIELD_NAME}
                        labelLeft={language.get(TelSysConfiguration.TelSysConfiguration_Field_ECCR_Replacement_Label)}
                        disabled={viewOnly || !isUserCMEWide || !isECCRReplacementEnabled}
                    />
                </FormRow>
            ) : (
                <></>
            )}
            {isAiAssistShown ? (
                <FormRow>
                    <RouterCheckbox
                        name={AI_ASSIST_ENABLED_FIELD_NAME}
                        labelLeft={language.get(TelSysConfiguration.TelSysConfiguration_Field_AIAssistEnabled_Label)}
                        disabled={viewOnly || !isRootCMEAdmin}
                    />
                </FormRow>
            ) : (
                <></>
            )}
            <FormRow>
                <ValidationErrors name={MFA_FIELD_NAME}>
                    <RouterCheckbox
                        name={MFA_FIELD_NAME}
                        labelLeft={language.get(TelSysConfiguration.TelSysConfiguration_Field_MFA_Label)}
                        disabled={viewOnly}
                    />
                </ValidationErrors>
            </FormRow>
            {isStringNotEmpty(cxiAccessCode) ? (
                <FormRow>
                    <div className='text-input'>
                        <span className='label access-code-label'>
                            {language.get(TelSysConfiguration.TelSysConfiguration_CXI_Access_Code)}
                        </span>
                        <span className='cxi-access-code'>
                            {cxiAccessCode}
                            <IconButton
                                iconName={IconName.COPY}
                                type={IconButtonType.BUTTON}
                                onClick={(): void => copyToClipboard(cxiAccessCode)}
                            />
                        </span>
                    </div>
                </FormRow>
            ) : (
                <></>
            )}
            {isEdit && isVarNotEmpty(commsStatus) ? <StatusOutput statuses={commsStatus} /> : <></>}
        </div>
    )
}
